import { fromEvent } from "file-selector"

/**
 * react-dropzone `getFilesFromEvent` 안전 래퍼 — 임베디드 웹뷰(권한 제한 컨텍스트) 드롭 폴백.
 *
 * 기본 구현(file-selector `fromEvent`)은 secure context 에서 `DataTransferItem.getAsFileSystemHandle()
 * → handle.getFile()` 경로를 try/catch 없이 탄다(2.1.2 기준). 임베디드 웹뷰가 FS 핸들 권한을 거부하면
 * NotAllowedError 로 드롭 전체가 실패한다 — http 로컬에선 insecure context 라 이 경로를 안 타 재현이 안 되고,
 * https 전환 후에만 나타나는 함정. 안정판 수정 릴리스 없음(2.1.2 가 latest, 3.0.0-beta 만 존재).
 *
 * 대응: drop 핸들러 동기 구간에서 파일 목록을 선스냅샷하고(await 이후엔 브라우저가 DataTransfer 를 비울 수
 * 있음), fromEvent 가 NotAllowedError 로 실패하면 스냅샷으로 폴백한다. 그 외 오류는 그대로 전파.
 *
 * 트레이드오프: 폴백 경로는 디렉토리 재귀 순회·숨김 파일 필터·path 보강이 없다 — 폴백이 발동하는
 * 컨텍스트에선 어차피 FS 핸들 접근이 불가하므로 수용.
 */
export async function safeFilesFromEvent(event: unknown): Promise<Array<File | DataTransferItem>> {
	const snapshot = snapshotEventFiles(event)
	try {
		return await fromEvent(event as Parameters<typeof fromEvent>[0])
	} catch (error) {
		if (error instanceof DOMException && error.name === "NotAllowedError" && snapshot.length > 0) {
			return snapshot
		}
		throw error
	}
}

/** DragEvent 의 dataTransfer.files 또는 파일 인풋 change 의 target.files 를 동기 추출 — duck-typing (React SyntheticEvent 대응). */
function snapshotEventFiles(event: unknown): File[] {
	const source = event as { dataTransfer?: DataTransfer | null; target?: { files?: FileList | null } | null } | null
	const dtFiles = source?.dataTransfer?.files
	if (dtFiles?.length) return Array.from(dtFiles)
	const inputFiles = source?.target?.files
	if (inputFiles?.length) return Array.from(inputFiles)
	return []
}
